import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  XCircle,
  CreditCard,
  PlusCircle,
  MinusCircle,
  Loader2,
  History,
  RotateCcw,
  Trash2,
  Edit2,
  Check,
  X,
  Clock,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";
import { domain } from "../../../security";
import AuditInfoModal, { buildAuditRows } from "../../shared/AuditInfoModal";
import Loader from "../../loader/Loader";

const formatDate = (d) =>
  d ? format(new Date(d), "MMM dd, yyyy hh:mm a") : "-";
const formatAmount = (amount) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(amount);

const CustomerCreditModal = ({ customer, onClose, onUpdate }) => {
  const currentUserID = useSelector(selectUserID);

  const [ledger, setLedger] = useState([]);
  const [history, setHistory] = useState([]);
  const [balance, setBalance] = useState(0);

  const [activeTab, setActiveTab] = useState("ledger");
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [auditItem, setAuditItem] = useState(null);

  const [form, setForm] = useState({
    transactionType: "ADD",
    amount: "",
    remarks: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    transactionType: "ADD",
    amount: "",
    remarks: "",
  });

  const fetchLedger = useCallback(async () => {
    try {
      setLoadingLedger(true);
      const res = await axios.get(
        `${domain}/api/CustomerCreditLimits/${customer.id}`,
      );
      setLedger(res.data.transactions);
      setBalance(res.data.availableBalance);
    } catch {
      toast.error("Failed to load credit ledger.");
    } finally {
      setLoadingLedger(false);
    }
  }, [customer.id]);

  const fetchHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const res = await axios.get(
        `${domain}/api/CustomerCreditLimits/${customer.id}/history`,
      );
      setHistory(res.data);
    } catch {
      toast.error("Failed to load transaction history.");
    } finally {
      setLoadingHistory(false);
    }
  }, [customer.id]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);
  useEffect(() => {
    if (activeTab === "history") fetchHistory();
  }, [activeTab, fetchHistory]);

  // Helper to call onUpdate after any successful mutation
  const handleUpdate = () => {
    if (onUpdate) onUpdate();
  };

  const handleAdd = async () => {
    const amount = parseFloat(form.amount);
    if (!form.amount || isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount greater than zero.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${domain}/api/CustomerCreditLimits`, {
        customerId: customer.id,
        amount,
        transactionType: form.transactionType,
        remarks: form.remarks || null,
        userID: currentUserID,
      });
      toast.success(
        `Credit ${form.transactionType === "ADD" ? "added" : "deducted"} successfully!`,
      );
      setForm({ transactionType: "ADD", amount: "", remarks: "" });
      fetchLedger();
      handleUpdate(); // ✅ notify parent
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save transaction.");
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      transactionType: row.transactionType,
      amount: Math.abs(row.amount).toString(),
      remarks: row.remarks || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ transactionType: "ADD", amount: "", remarks: "" });
  };

  const handleUpdateTransaction = async (id) => {
    const amount = parseFloat(editForm.amount);
    if (!editForm.amount || isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount greater than zero.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.put(`${domain}/api/CustomerCreditLimits/${id}`, {
        amount,
        transactionType: editForm.transactionType,
        remarks: editForm.remarks || null,
        userID: currentUserID,
      });
      toast.success("Transaction updated successfully!");
      cancelEdit();
      fetchLedger();
      handleUpdate(); // ✅ notify parent
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update transaction.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this transaction?"))
      return;
    try {
      await axios.put(`${domain}/api/CustomerCreditLimits/soft-delete/${id}`, {
        userID: currentUserID,
      });
      toast.success("Transaction moved to history.");
      fetchLedger();
      handleUpdate(); // ✅ notify parent
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to delete transaction.",
      );
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${domain}/api/CustomerCreditLimits/restore/${id}`, {
        userID: currentUserID,
      });
      toast.success("Transaction restored successfully!");
      fetchHistory();
      fetchLedger();
      handleUpdate(); // ✅ notify parent
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to restore transaction.",
      );
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white shrink-0">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-indigo-50 rounded-full text-indigo-600">
              <CreditCard size={18} />
            </div>
            Credit Ledger — {customer.customerName}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 bg-slate-50/50 flex flex-col gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between shadow-sm shrink-0">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">
                Available Credit Balance
              </p>
              <p
                className={`text-3xl font-bold ${
                  balance < 0 ? "text-red-600" : "text-emerald-600"
                }`}
              >
                {formatAmount(balance)}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {customer.customerName} · {customer.customerCode}
              </p>
            </div>
            <div
              className={`p-4 rounded-xl ${
                balance < 0
                  ? "bg-red-50 text-red-400"
                  : "bg-emerald-50 text-emerald-500"
              }`}
            >
              {balance < 0 ? (
                <TrendingDown size={32} />
              ) : (
                <TrendingUp size={32} />
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm shrink-0">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              New Transaction
            </h3>
            <div className="flex gap-2 mb-3">
              <button
                onClick={() =>
                  setForm((f) => ({ ...f, transactionType: "ADD" }))
                }
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  form.transactionType === "ADD"
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                    : "bg-white text-slate-500 border-slate-200 hover:border-emerald-300"
                }`}
              >
                <PlusCircle size={15} /> Add Credit
              </button>
              <button
                onClick={() =>
                  setForm((f) => ({ ...f, transactionType: "DEDUCT" }))
                }
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  form.transactionType === "DEDUCT"
                    ? "bg-red-600 text-white border-red-600 shadow-sm"
                    : "bg-white text-slate-500 border-slate-200 hover:border-red-300"
                }`}
              >
                <MinusCircle size={15} /> Deduct Credit
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Amount (₱)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amount: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-300 outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Remarks
                </label>
                <input
                  type="text"
                  placeholder="e.g. Initial credit setup"
                  value={form.remarks}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, remarks: e.target.value }))
                  }
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-300 outline-none"
                />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={handleAdd}
                disabled={submitting}
                className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl text-white shadow-sm transition disabled:opacity-60 ${
                  form.transactionType === "ADD"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {submitting ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> Saving...
                  </>
                ) : form.transactionType === "ADD" ? (
                  <>
                    <PlusCircle size={15} /> Add Credit
                  </>
                ) : (
                  <>
                    <MinusCircle size={15} /> Deduct Credit
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex border-b border-slate-100">
              <button
                onClick={() => setActiveTab("ledger")}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all ${
                  activeTab === "ledger"
                    ? "text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <CreditCard size={15} /> Active Transactions{" "}
                <span className="ml-1 text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-bold">
                  {ledger.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab("history")}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all ${
                  activeTab === "history"
                    ? "text-red-600 border-b-2 border-red-500 bg-red-50/50"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <History size={15} /> Deleted History{" "}
                <span className="ml-1 text-xs bg-red-100 text-red-500 px-1.5 py-0.5 rounded-full font-bold">
                  {history.length}
                </span>
              </button>
            </div>

            {activeTab === "ledger" && (
              <>
                {loadingLedger ? (
                  <div className="flex justify-center py-10">
                    <Loader />
                  </div>
                ) : ledger.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 text-sm">
                    <CreditCard size={28} className="mx-auto mb-2 opacity-30" />
                    No transactions yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-slate-600">
                      <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Type</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3">Remarks</th>
                          <th className="px-4 py-3">Created By</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {ledger.map((row) =>
                          editingId === row.id ? (
                            <tr key={row.id} className="bg-indigo-50/40">
                              <td className="px-4 py-2 text-xs text-slate-400">
                                {formatDate(row.transactionDate)}
                              </td>
                              <td className="px-4 py-2">
                                <select
                                  value={editForm.transactionType}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      transactionType: e.target.value,
                                    }))
                                  }
                                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-300 outline-none bg-white"
                                >
                                  <option value="ADD">ADD</option>
                                  <option value="DEDUCT">DEDUCT</option>
                                </select>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editForm.amount}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      amount: e.target.value,
                                    }))
                                  }
                                  className="w-28 text-right text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-300 outline-none"
                                />
                              </td>
                              <td className="px-4 py-2" colSpan={2}>
                                <input
                                  type="text"
                                  placeholder="Remarks"
                                  value={editForm.remarks}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      remarks: e.target.value,
                                    }))
                                  }
                                  className="w-full text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-indigo-300 outline-none"
                                />
                              </td>
                              <td className="px-4 py-2 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() =>
                                      handleUpdateTransaction(row.id)
                                    }
                                    disabled={submitting}
                                    className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition disabled:opacity-50"
                                  >
                                    {submitting ? (
                                      <Loader2
                                        size={15}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <Check size={15} />
                                    )}
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg transition"
                                  >
                                    <X size={15} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            <tr
                              key={row.id}
                              className="hover:bg-slate-50 transition"
                            >
                              <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                                {formatDate(row.transactionDate)}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    row.transactionType === "ADD"
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-red-100 text-red-600"
                                  }`}
                                >
                                  {row.transactionType === "ADD" ? (
                                    <PlusCircle size={10} />
                                  ) : (
                                    <MinusCircle size={10} />
                                  )}
                                  {row.transactionType}
                                </span>
                              </td>
                              <td
                                className={`px-4 py-3 text-right font-mono font-semibold whitespace-nowrap ${
                                  row.amount >= 0
                                    ? "text-emerald-600"
                                    : "text-red-600"
                                }`}
                              >
                                {formatAmount(row.amount)}
                              </td>
                              <td className="px-4 py-3 text-xs text-slate-500 max-w-[160px] truncate">
                                {row.remarks || "—"}
                              </td>
                              <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                                {row.createdByFullName || "System"}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => setAuditItem(row)}
                                    className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition"
                                  >
                                    <Clock size={15} />
                                  </button>
                                  <button
                                    onClick={() => startEdit(row)}
                                    className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                  >
                                    <Edit2 size={15} />
                                  </button>
                                  <button
                                    onClick={() => handleSoftDelete(row.id)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {activeTab === "history" && (
              <>
                {loadingHistory ? (
                  <div className="flex justify-center py-10">
                    <Loader />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-12 text-slate-400 text-sm">
                    <History size={28} className="mx-auto mb-2 opacity-30" />
                    No deleted transactions found.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-slate-600">
                      <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3">Date</th>
                          <th className="px-4 py-3">Type</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3">Remarks</th>
                          <th className="px-4 py-3">Deleted By</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {history.map((row) => (
                          <tr
                            key={row.id}
                            className="hover:bg-slate-50 transition opacity-75"
                          >
                            <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
                              {formatDate(row.transactionDate)}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold opacity-60 ${
                                  row.transactionType === "ADD"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-red-100 text-red-600"
                                }`}
                              >
                                {row.transactionType}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-mono font-semibold text-slate-400 whitespace-nowrap line-through">
                              {formatAmount(row.amount)}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-400 max-w-[140px] truncate">
                              {row.remarks || "—"}
                            </td>
                            <td className="px-4 py-3 text-xs">
                              <span className="text-red-500 font-medium">
                                {row.deletedByFullName || "Unknown"}
                              </span>
                              <div className="text-slate-400 text-[10px]">
                                {formatDate(row.deletedDate)}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => setAuditItem(row)}
                                  className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition"
                                >
                                  <Clock size={15} />
                                </button>
                                <button
                                  onClick={() => handleRestore(row.id)}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-emerald-600 hover:bg-emerald-50 rounded-lg transition border border-emerald-200"
                                >
                                  <RotateCcw size={13} /> Restore
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={`Transaction #${auditItem?.id}`}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default CustomerCreditModal;
